import { tokenize } from './lexer';
import { type Token, TokenKind } from './tokens';

/**
 * How deeply a document nests — measured **iteratively**, over tokens, before anything
 * recursive is allowed near the input.
 *
 * This module exists for one measured reason (ADR-0007): `jsonc-parser`'s `parseTree` and
 * `visit` are recursive descent and throw `RangeError: Maximum call stack size exceeded`
 * at nesting depth **3928** on Node 22 — and a Worker thread's stack can be smaller still
 * (ADR-0010). A crash is not an acceptable answer to a hostile 5 MB file of open brackets,
 * so depth has to be knowable *before* the parser is called. The scanner is iterative and
 * survives any depth, so counting brackets over its output always terminates.
 *
 * The consequence for the pipeline: step 2 measures, and if the document is deeper than
 * `LintOptions.maxDepth` the parse step is skipped entirely and the user gets a
 * `semantic/deep-nesting` diagnostic instead of a white screen.
 */

/** What a depth scan learned. Offsets are UTF-16 code units, `-1` meaning "not reached". */
export interface DepthScan {
  /** Greatest nesting depth reached. `0` for a scalar, an empty document or pure garbage. */
  readonly maxDepth: number;

  /**
   * Offset of the `{` or `[` that first reached {@link maxDepth}, or `-1` when the
   * document nests nowhere. This is where a "too deep" diagnostic is anchored.
   */
  readonly deepestOffset: number;

  /**
   * Offset of the first `{` or `[` that pushed the depth *past* the given limit, or `-1`
   * when the limit was never exceeded. Always `-1` when no limit was given.
   */
  readonly exceededAt: number;
}

/**
 * Maximum nesting depth of `source`, or of an already-scanned token stream.
 *
 * Pass tokens when you have them — the pipeline lexes once and reuses the array — and a
 * string only when you do not. Never throws and never recurses, at any depth.
 */
export function depthOf(input: string | readonly Token[]): number {
  return scanDepth(input).maxDepth;
}

/**
 * {@link depthOf} plus the offsets a diagnostic needs.
 *
 * `limit` is optional: pass `LintOptions.maxDepth` to learn *where* the document first got
 * too deep in the same single pass. The scan always runs to the end, so `maxDepth` is the
 * true depth of the document even when `limit` was blown past early.
 *
 * Unbalanced closers cannot drive the counter negative: a `}` at depth 0 is ignored rather
 * than counted as `-1`, so `]]]]{` reports depth 1 and not depth −2. Brackets inside string
 * literals are invisible here because a string is a single token — which is exactly why
 * this counts tokens and not characters.
 */
export function scanDepth(
  input: string | readonly Token[],
  limit = Number.MAX_SAFE_INTEGER,
): DepthScan {
  const tokens = typeof input === 'string' ? tokenize(input) : input;

  let depth = 0;
  let maxDepth = 0;
  let deepestOffset = -1;
  let exceededAt = -1;

  for (const token of tokens) {
    if (token.kind === TokenKind.OpenBrace || token.kind === TokenKind.OpenBracket) {
      depth += 1;

      if (depth > maxDepth) {
        maxDepth = depth;
        deepestOffset = token.from;
      }
      if (exceededAt === -1 && depth > limit) {
        exceededAt = token.from;
      }
    } else if (token.kind === TokenKind.CloseBrace || token.kind === TokenKind.CloseBracket) {
      if (depth > 0) depth -= 1;
    }
  }

  return { maxDepth, deepestOffset, exceededAt };
}
